import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { PasswordInput } from '../components/Auth/PasswordInput'
import { Key, Loader2, CheckCircle, AlertCircle } from 'lucide-react'

export const SetPassword: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  // Extract tokens from URL (Supabase sends them in hash fragment)
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [refreshToken, setRefreshToken] = useState<string | null>(null)

  useEffect(() => {
    // Tokens are in the hash fragment: #access_token=xxx&refresh_token=yyy
    const hashParams = new URLSearchParams(window.location.hash.substring(1))
    const access = hashParams.get('access_token') || searchParams.get('access_token')
    const refresh = hashParams.get('refresh_token') || searchParams.get('refresh_token')

    setAccessToken(access)
    setRefreshToken(refresh)

    if (!access || !refresh) {
      setError('Invalid or expired password setup link')
    }
  }, [searchParams])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validation
    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setLoading(true)

    try {
      // Set the session from recovery tokens
      const { error: sessionError } = await supabase.auth.setSession({
        access_token: accessToken!,
        refresh_token: refreshToken!,
      })

      if (sessionError) throw sessionError

      // Update password
      const { error: updateError } = await supabase.auth.updateUser({
        password: password,
      })

      if (updateError) throw updateError

      setSuccess(true)

      // Redirect to chat after 2 seconds
      setTimeout(() => {
        navigate('/chat')
      }, 2000)

    } catch (err) {
      console.error('Error setting password:', err)
      setError(err instanceof Error ? err.message : 'Failed to set password')
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-dark-primary via-dark-secondary to-dark-tertiary flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-dark-secondary/50 backdrop-blur-sm border border-primary-600/20 rounded-3xl p-12 text-center">
          <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-light-primary mb-4">
            Password Set Successfully!
          </h1>
          <p className="text-light-secondary mb-4">
            Redirecting you to your account...
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-primary via-dark-secondary to-dark-tertiary flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-dark-secondary/50 backdrop-blur-sm border border-primary-600/20 rounded-3xl p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-primary-600 to-secondary-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Key className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-light-primary mb-2">
              Set Your Password
            </h1>
            <p className="text-light-secondary">
              Create a password to complete your account setup
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center space-x-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-light-secondary mb-2">
                Password
              </label>
              <PasswordInput
                id="password"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                autoComplete="new-password"
                disabled={loading || !accessToken}
              />
              <p className="mt-1 text-xs text-light-tertiary">
                Must be at least 8 characters
              </p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-light-secondary mb-2">
                Confirm Password
              </label>
              <PasswordInput
                id="confirmPassword"
                name="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                autoComplete="new-password"
                disabled={loading || !accessToken}
              />
            </div>

            <button
              type="submit"
              disabled={loading || !accessToken || !refreshToken}
              className="
                w-full flex items-center justify-center space-x-2 px-6 py-4
                bg-gradient-to-r from-primary-600 to-secondary-600
                hover:from-primary-700 hover:to-secondary-700
                disabled:from-gray-600 disabled:to-gray-700
                text-white rounded-xl font-semibold text-lg
                transition-all transform hover:scale-105
                disabled:transform-none disabled:cursor-not-allowed
              "
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Setting Password...</span>
                </>
              ) : (
                <>
                  <Key className="w-5 h-5" />
                  <span>Set Password & Continue</span>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
